<template>
    <TransitionGroup
        tag="div"
        :enterFromClass="$style.fade_enter_from"
        :enterActiveClass="$style.fade_enter_active"
        :moveClass="$style.fade_move"
        :leaveActiveClass="$style.fade_leave_active"
        :leaveToClass="$style.fade_leave_to"
        :class="$style.header"
        data-interface-section="mobile-header"
    >
        <div
            v-if="!isSearchOpened"
            key="left-menu"
            :class="$style.leftBlock"
        >
            <div :class="$style.menu">
                <div :class="$style.short_menu">
                    <!-- read it from props to keep normal menu under the copy in the ModalCategories -->
                    <template v-if="props.categoriesModalMode">
                        <Button
                            iconLeft="close"
                            visual="ghost"
                            color="purple-500"
                            interfaceTarget="menu-close"
                            @click="menuToggle"
                        >
                            {{ $tr('mobileHeader.close') }}
                        </Button>
                    </template>
                    <template v-else>
                        <button
                            style="display: flex; padding: 0"
                            data-interface-target="menu"
                            @click="menuToggle"
                        >
                            <Icon
                                name="menu"
                                size="m"
                            />
                        </button>
                        <div
                            ref="logoWrapRef"
                            :class="$style.logo_wrapper"
                        >
                            <Logo
                                :class="$style.logo_override"
                                :noLink="isMain"
                                mode="default"
                            />
                            <Logo
                                v-if="isMounted"
                                :noLink="isMain"
                                mode="short"
                                :class="$style.logo_short_override"
                                hidden
                            />
                        </div>
                    </template>
                </div>
                <div :class="$style.wide_menu">
                    <Logo
                        :class="$style.logo_override"
                        :noLink="isMain"
                        mode="default"
                    />
                    <Button
                        iconLeft="menu"
                        size="l"
                        visual="ghost"
                        color="purple-500"
                        interfaceTarget="menu"
                        @click="menuToggle"
                    >
                        {{ $tr('mobileHeader.all_games') }}
                    </Button>
                </div>
            </div>
        </div>
        <Search
            v-if="!props.categoriesModalMode && isMounted"
            key="search"
            :inputClass="$style.search"
            @open="isSearchOpened = true"
            @close="isSearchOpened = false"
        />
        <div
            v-if="isUserInfoLoaded && isMounted && !isSearchOpened"
            key="user"
            ref="rightBlockRef"
            :class="$style.rightBlock"
        >
            <template v-if="isAuthorized && user">
                <template v-if="route.name === ROUTE_NAMES.PROFILE">
                    <UserAvatar
                        :class="$style.avatar_override"
                        :avatar="user.avatar"
                        :firstName="user.firstName"
                        :lastName="user.lastName"
                    />
                    <Button
                        color="carbon-400"
                        size="m"
                        icon="logout"
                        interfaceTarget="logout"
                        @click="logout"
                    />
                </template>
                <template v-else>
                    <BaseLink
                        :to="{ name: ROUTE_NAMES.PROFILE }"
                        data-interface-target="profile-link"
                    >
                        <UserAvatar
                            :class="$style.avatar_override"
                            :avatar="user.avatar"
                            :firstName="user.firstName"
                            :lastName="user.lastName"
                        />
                    </BaseLink>
                    <BaseLink
                        :to="{ name: ROUTE_NAMES.PROFILE }"
                        :class="$style.coins"
                        data-interface-target="profile-link"
                    >
                        <Icon
                            name="silver_fennec_coin_small"
                            :class="$style.coins_icon"
                        />
                        <Typography
                            type="header"
                            size="xs"
                            :responsive="false"
                            :class="$style.fennecs"
                        >
                            <Balance :balance="userBalance" />
                        </Typography>
                    </BaseLink>
                </template>
            </template>
            <template v-else>
                <Button
                    size="m"
                    color="salad"
                    interfaceTarget="open-sign-in-modal"
                    @click="signin"
                >
                    {{ $tr('mobileHeader.login') }}
                </Button>
            </template>
        </div>
    </TransitionGroup>
</template>
<script setup lang="ts">
import { useDebounceFn, useResizeObserver } from '@vueuse/core'

import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, useCssModule } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Balance from '@/components_new/Balance.vue'
import BaseLink from '@/components_new/BaseLink.vue'
import Button from '@/components_new/Button.vue'
import Icon from '@/components_new/Icon/Icon.vue'
import Logo from '@/components_new/Logo/Logo.vue'
import Typography from '@/components_new/Typography.vue'
import UserAvatar from '@/components_new/UserAvatar.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useLayoutStore } from '@/store/layout-store'
import { useUserStore } from '@/store/user-store/user-store'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import Search from '../Search/Search.vue'

const isSearchOpened = ref(false)

// to control state from the parent component and let two headers co-exist in differen states
const props = withDefaults(
    defineProps<{
        categoriesModalMode?: boolean
    }>(),
    {
        categoriesModalMode: false,
    },
)

const logoWrapRef = ref<HTMLElement | null>(null)

const userStore = useUserStore()
const styles = useCssModule()

const route = useRoute()
const router = useRouter()
const isMain = computed(() => route.name === ROUTE_NAMES.MAIN)

function logout() {
    userStore.logout()
    router.replace({ name: ROUTE_NAMES.SHOP })
}

const { user, /* taskStats, */ isAuthorized, userBalance, isUserInfoLoaded } = storeToRefs(userStore)

const layoutStore = useLayoutStore()

const { categoriesModalOpened } = storeToRefs(layoutStore)
function menuToggle() {
    layoutStore.setCategoriesModalOpened(!categoriesModalOpened.value)
}

function signin() {
    layoutStore.setSignInModalOpened(true)
}

function onResize() {
    const logoWrapRefWidth = logoWrapRef.value?.clientWidth
    const logo = logoWrapRef.value?.querySelector<HTMLElement>(`.${styles.logo_override}`)
    const logoShort = logoWrapRef.value?.querySelector<HTMLElement>(`.${styles.logo_short_override}`)
    if (!logo || !logoShort || !logoWrapRefWidth) {
        return
    }
    logo.hidden = false
    logoShort.hidden = true
    if (logoWrapRefWidth <= logo.clientWidth) {
        logo.hidden = true
        logoShort.hidden = false
    }
}

const debouncedOnResize = useDebounceFn(onResize, 50)
useResizeObserver(logoWrapRef, debouncedOnResize)

const isMounted = useIsMounted()
onMounted(() => {
    onResize()
})
</script>

<style module critical>
.header {
    gap: 8px;
    height: var(--global-header-pannel-height);
    padding: env(safe-area-inset-top) 0 0;
}
</style>
<style module>
.header,
.leftBlock,
.rightBlock,
.coins,
.wide_menu,
.short_menu {
    display: flex;
    align-items: center;
}

.menu,
.logo_wrapper {
    display: flex;
    flex-grow: 1;
}
/* for querySelector in onResize callback - otherwise we won't find elements by these classes */
.logo_override,
.logo_short_override {
    visibility: visible;
}

.leftBlock {
    gap: 16px;
    flex-grow: 1;
}

.wide_menu {
    gap: 12px;
}

.short_menu {
    color: #aa76ff;
    gap: 8px;
    display: none;
}

.rightBlock {
    gap: 12px;
}

.coins {
    gap: 8px;
}

.coins_icon {
    width: 32px;
    height: 32px;
}

.avatar_override {
    width: 32px;
}

.fennecs {
    color: var(--lavander-600);
}

.search {
    width: 40px;
    height: 40px;
}

.wide_menu {
    display: none;
}

.short_menu {
    display: flex;
    flex-grow: 1;
}

@media (--mobile) {
    .rightBlock {
        gap: 8px;
    }

    .coins {
        font-size: 16px;
        gap: 6px;
    }
}

/* temp. move wide menu to fit all items in the header */
@media (width >= 620px) and (height > 480px) {
    .short_menu {
        display: none;
    }
    .wide_menu {
        display: flex;
    }
}

.fade_move,
.fade_enter_active,
.fade_leave_active {
    transition: transform 200ms ease-out;
}

.fade_enter_from,
.fade_leave_to {
    opacity: 0;
    transform: translate(5%, 0);
}

/* ensure leaving items are taken out of layout flow so that moving animations can be calculated correctly. */
.fade_leave_active {
    position: absolute;
}
</style>
